<template>
  <div>
    <b-card
      v-if="loadError"
      class="mb-0"
    >
      <b-alert
        show
        variant="danger"
      >
        {{ loadError }}
      </b-alert>
    </b-card>

    <template v-else>
      <b-card class="mb-2">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="CalendarIcon"
            size="28"
            class="mr-1 text-primary flex-shrink-0"
          />
          <p class="mb-0 lead font-weight-bold">
            {{ headerLine }}
          </p>
        </div>
      </b-card>

      <b-row>
        <b-col
          cols="12"
          lg="5"
          xl="4"
        >
          <h6 class="text-body font-weight-bold mb-1">
            Wybierz termin
          </h6>
          <b-card
            no-body
            class="mb-2 enrol-calendar-card"
          >
            <b-card-body class="pb-2">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <b-button
                  variant="flat-secondary"
                  size="sm"
                  class="btn-icon enrol-calendar-nav-btn"
                  @click="shiftMonth(-1)"
                >
                  <feather-icon icon="ChevronLeftIcon" />
                </b-button>
                <div class="d-flex align-items-center flex-wrap justify-content-center">
                  <b-form-select
                    v-model="calendarMonth"
                    :options="monthOptions"
                    size="sm"
                    class="mr-50 mb-50 mb-sm-0"
                    style="width: auto; min-width: 8rem"
                    @change="onMonthYearChange"
                  />
                  <b-form-select
                    v-model="calendarYear"
                    :options="yearOptions"
                    size="sm"
                    style="width: auto; min-width: 5rem"
                    @change="onMonthYearChange"
                  />
                </div>
                <b-button
                  variant="flat-secondary"
                  size="sm"
                  class="btn-icon enrol-calendar-nav-btn"
                  @click="shiftMonth(1)"
                >
                  <feather-icon icon="ChevronRightIcon" />
                </b-button>
              </div>

              <div class="calendar-weekdays text-muted small d-flex">
                <span
                  v-for="wd in weekDayLabels"
                  :key="wd"
                  class="calendar-cell weekday"
                >{{ wd }}</span>
              </div>
              <div
                v-for="(week, wi) in calendarWeeks"
                :key="wi"
                class="d-flex"
              >
                <button
                  v-for="day in week"
                  :key="day.key"
                  type="button"
                  class="calendar-cell day-cell"
                  :class="dayCellClass(day)"
                  :disabled="day.outsideMonth"
                  @click="selectDay(day)"
                >
                  <span class="day-num">{{ day.date.date() }}</span>
                  <span
                    v-if="day.hasAvailability"
                    class="slot-dot"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col
          cols="12"
          lg="7"
          xl="8"
        >
          <h6 class="text-body font-weight-bold mb-1">
            Dostępne terminy
          </h6>
          <b-card
            class="mb-0"
            body-class="py-2 px-2"
          >
            <div v-if="loading">
              <b-spinner class="mr-1" />
              Ładowanie…
            </div>
            <template v-else-if="!selectedDate">
              <p class="text-muted mb-0">
                Wybierz dzień, aby sprawdzić godziny certyfikacji.
              </p>
            </template>
            <template v-else>
              <p class="text-muted small mb-1">
                Wybrana data: <span class="text-body font-weight-bold">{{ selectedDateLabel }}</span>
              </p>
              <template v-if="!dayBlocks.length">
                <p class="text-muted mb-0">
                  Brak terminów certyfikacji w tym dniu (wolnych lub zajętych) w ramach dostępnych dla zapisu certyfikacji.
                </p>
              </template>
              <template v-else>
                <div
                  v-for="block in dayBlocks"
                  :key="block.certificationId"
                  class="enrol-cert-block"
                >
                  <ul class="list-unstyled small enrol-cert-meta-list mb-1">
                    <li>
                      <feather-icon
                        icon="AwardIcon"
                        size="16"
                        class="mr-50 text-muted flex-shrink-0"
                      />
                      <span><span class="font-weight-bold">Certyfikacja:</span> {{ block.certificationName }}</span>
                    </li>
                    <li>
                      <feather-icon
                        icon="DollarSignIcon"
                        size="16"
                        class="mr-50 text-muted flex-shrink-0"
                      />
                      <span>
                        <span class="font-weight-bold">Koszt:</span>
                        <template v-if="block.isFree">
                          Darmowa
                        </template>
                        <template v-else>
                          {{ block.priceInGrosze | priceInGrosze }} netto / os.
                        </template>
                      </span>
                    </li>
                    <li
                      v-if="block.examinerName"
                    >
                      <feather-icon
                        icon="UserIcon"
                        size="16"
                        class="mr-50 text-muted flex-shrink-0"
                      />
                      <span><span class="font-weight-bold">Egzaminator:</span> {{ block.examinerName }}</span>
                    </li>
                    <li>
                      <feather-icon
                        icon="MapPinIcon"
                        size="16"
                        class="mr-50 text-muted flex-shrink-0"
                      />
                      <span><span class="font-weight-bold">Lokalizacja:</span> {{ block.localization }}</span>
                    </li>
                    <li>
                      <feather-icon
                        icon="ExternalLinkIcon"
                        size="16"
                        class="mr-50 text-muted flex-shrink-0"
                      />
                      <span>
                        <router-link
                          :to="{ name: 'certifications-detail', params: { id: block.certificationId } }"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="enrol-cert-detail-link"
                        >
                          Szczegóły certyfikacji
                        </router-link>
                      </span>
                    </li>
                  </ul>
                  <p
                    v-if="block.descriptionText"
                    class="small text-muted border-top pt-1 mb-1"
                  >
                    {{ block.descriptionText }}
                  </p>
                  <p class="small font-weight-bold text-body mb-50">
                    Dostępne godziny
                  </p>
                  <div class="enrol-slot-list">
                    <div
                      v-for="sl in block.slots"
                      :key="sl.id"
                      class="enrol-slot-tile d-flex align-items-center justify-content-between"
                    >
                      <div class="enrol-slot-time text-body font-weight-bold">
                        {{ formatSlotClock(sl.time) }}
                      </div>
                      <div class="flex-shrink-0">
                        <b-button
                          v-if="isSlotBookable(sl)"
                          variant="primary"
                          size="sm"
                          @click="openConfirm(sl, block)"
                        >
                          Zapisz
                        </b-button>
                        <b-button
                          v-else-if="sl.taken === true"
                          variant="primary"
                          size="sm"
                          class="enrol-slot-btn-full"
                          disabled
                        >
                          Zajęty
                        </b-button>
                        <b-button
                          v-else
                          variant="outline-secondary"
                          size="sm"
                          disabled
                        >
                          Niedostępne
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <b-modal
      id="modal-confirm-cert-enrol"
      v-model="confirmVisible"
      title="Potwierdzenie zapisu"
      hide-footer
      centered
      @hidden="onConfirmModalHidden"
    >
      <p class="mb-3">
        {{ confirmMessage }}
      </p>
      <div class="d-flex justify-content-end">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="confirmVisible = false"
        >
          NIE
        </b-button>
        <b-button
          variant="primary"
          :disabled="confirmLoading"
          @click="onConfirmYes"
        >
          <b-spinner
            v-if="confirmLoading"
            small
            class="mr-50"
          />
          TAK
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert, BButton, BCard, BCardBody, BCol, BFormSelect, BModal, BRow, BSpinner,
} from 'bootstrap-vue'
import dayjs from 'dayjs'
import 'dayjs/locale/pl'
import isoWeek from 'dayjs/plugin/isoWeek'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import localizedFormat from 'dayjs/plugin/localizedFormat'
import {
  certificationKindGenitive,
  isCertificationSlotBookable,
  formatCertificationSlotTime,
  formatCertificationSlotClock,
  datesWithAvailabilitySet,
  certificationsForDay,
  buildCertificationCalendarWeeks,
  buildCertificationEnrolConfirmMessage,
  resolveEnrolmentBootstrapError,
  buildEnrolmentHeaderLine,
} from '@/helpers/certification/certificationEnrolLogic'

dayjs.extend(isoWeek)
dayjs.extend(localizedFormat)
dayjs.locale('pl')

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormSelect,
    BModal,
    BRow,
    BSpinner,
  },
  data() {
    return {
      loading: false,
      loadError: null,
      options: null,
      selectedDate: null,
      calendarMonth: dayjs().month() + 1,
      calendarYear: dayjs().year(),
      confirmVisible: false,
      confirmLoading: false,
      pendingSlotIri: null,
      pendingSlotTime: null,
      pendingBlock: null,
    }
  },
  computed: {
    userId() {
      return this.$route.params.userId
    },
    headerLine() {
      return buildEnrolmentHeaderLine(this.options)
    },
    datesSet() {
      return datesWithAvailabilitySet(this.options)
    },
    dayBlocks() {
      return certificationsForDay(this.options)
    },
    selectedDateLabel() {
      if (!this.selectedDate) {
        return ''
      }
      return dayjs(this.selectedDate).locale('pl').format('D MMMM YYYY')
    },
    monthOptions() {
      return [
        { value: 1, text: 'Styczeń' },
        { value: 2, text: 'Luty' },
        { value: 3, text: 'Marzec' },
        { value: 4, text: 'Kwiecień' },
        { value: 5, text: 'Maj' },
        { value: 6, text: 'Czerwiec' },
        { value: 7, text: 'Lipiec' },
        { value: 8, text: 'Sierpień' },
        { value: 9, text: 'Wrzesień' },
        { value: 10, text: 'Październik' },
        { value: 11, text: 'Listopad' },
        { value: 12, text: 'Grudzień' },
      ]
    },
    yearOptions() {
      const y = dayjs().year()
      const out = []
      for (let i = y - 1; i <= y + 3; i += 1) {
        out.push({ value: i, text: String(i) })
      }
      return out
    },
    visibleMonth() {
      return dayjs(`${this.calendarYear}-${String(this.calendarMonth).padStart(2, '0')}-01`)
    },
    weekDayLabels() {
      const start = dayjs().locale('pl').startOf('isoWeek')
      return Array.from({ length: 7 }, (_, i) => start.add(i, 'day').format('ddd'))
    },
    calendarWeeks() {
      return buildCertificationCalendarWeeks(this.visibleMonth, this.datesSet)
    },
    confirmMessage() {
      return buildCertificationEnrolConfirmMessage(this.options, this.pendingSlotTime)
    },
  },
  watch: {
    '$route.params.userId': function onRouteUserIdChange() {
      this.bootstrap()
    },
  },
  mounted() {
    this.$root.$emit('pageTitle:set', 'Zapis na certyfikację')
    this.bootstrap()
  },
  methods: {
    kindGenitive(kind) {
      return certificationKindGenitive(kind)
    },
    shiftMonth(delta) {
      const d = this.visibleMonth.add(delta, 'month')
      this.calendarMonth = d.month() + 1
      this.calendarYear = d.year()
    },
    onMonthYearChange() {
      /* v-model updated */
    },
    dayCellClass(day) {
      const ymd = day.date.format('YYYY-MM-DD')
      const active = this.selectedDate === ymd
      return {
        'is-outside': day.outsideMonth,
        'is-selected': active,
        'is-today': day.date.isSame(dayjs(), 'day'),
      }
    },
    selectDay(day) {
      if (day.outsideMonth) {
        this.calendarMonth = day.date.month() + 1
        this.calendarYear = day.date.year()
      }
      const ymd = day.date.format('YYYY-MM-DD')
      this.selectedDate = ymd
      this.loadOptions(ymd)
    },
    isSlotBookable(sl) {
      return isCertificationSlotBookable(sl)
    },
    formatSlotClock(iso) {
      return formatCertificationSlotClock(iso)
    },
    formatSlotTime(iso) {
      return formatCertificationSlotTime(iso)
    },
    openConfirm(slot, block) {
      this.pendingSlotIri = `/certification_slots/${slot.id}`
      this.pendingSlotTime = slot.time
      this.pendingBlock = block
      this.confirmLoading = false
      this.confirmVisible = true
    },
    onConfirmModalHidden() {
      this.pendingSlotIri = null
      this.pendingSlotTime = null
      this.pendingBlock = null
      this.confirmLoading = false
    },
    onConfirmYes() {
      if (!this.pendingSlotIri || !this.options) {
        return
      }
      this.confirmLoading = true
      this.$store.dispatch('user/fetchUser', { id: this.userId }).then(userResp => {
        if (!userResp || userResp.status !== 200) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Nie udało się pobrać użytkownika',
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
          this.confirmLoading = false
          return null
        }
        const iri = userResp.data && userResp.data['@id']
        if (!iri) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Brak danych użytkownika',
              text: 'Odśwież listę certyfikatów i spróbuj ponownie.',
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
          this.confirmLoading = false
          return null
        }
        return this.$store.dispatch('certification/addEnrolment', {
          slot: this.pendingSlotIri,
          user: iri,
        })
      }).then(enrolResp => {
        if (!enrolResp) {
          return
        }
        if (enrolResp.status === 201) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Zapisano na certyfikację',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.confirmVisible = false
          this.$router.push({ name: 'certifications-list' })
        } else {
          const msg = enrolResp.data && enrolResp.data['hydra:description']
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Nie udało się zapisać',
              text: msg || null,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
            timeout: false,
          })
        }
      }).finally(() => {
        this.confirmLoading = false
      })
    },
    loadOptions(dateYmd) {
      this.loading = true
      this.$store.dispatch('certification/fetchEnrolmentOptions', {
        userId: this.userId,
        date: dateYmd,
      }).then(response => {
        if (response && response.status === 200) {
          this.options = response.data
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Nie udało się pobrać slotów',
              text: response && response.data && response.data['hydra:description']
                ? response.data['hydra:description']
                : null,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
            timeout: false,
          })
        }
      }).catch(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Nie udało się pobrać slotów',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }).finally(() => {
        this.loading = false
      })
    },
    bootstrap() {
      this.loadError = null
      this.options = null
      this.selectedDate = null
      this.pendingSlotIri = null
      this.pendingSlotTime = null
      this.pendingBlock = null
      this.loading = true
      this.$store.dispatch('certification/fetchEnrolmentOptions', {
        userId: this.userId,
        date: null,
      }).then(response => {
        if (response && response.status === 200) {
          this.options = response.data
        } else {
          this.loadError = resolveEnrolmentBootstrapError(response)
        }
      }).catch(() => {
        this.loadError = resolveEnrolmentBootstrapError(null)
      }).finally(() => {
        this.loading = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.enrol-calendar-card {
  /* flat-secondary w motywie = pomarańczowy; tu ten sam niebieski co kalendarz (#0067c0) */
  ::v-deep .enrol-calendar-nav-btn.btn-flat-secondary {
    color: #0067c0 !important;

    svg {
      color: inherit;
    }

    &:hover:not(:disabled),
    &:focus:not(:disabled) {
      color: #005099 !important;
      background-color: rgba(0, 103, 192, 0.12) !important;
    }
  }

  .calendar-weekdays {
    margin-bottom: 0.25rem;
  }

  .calendar-cell {
    flex: 1 1 calc(100% / 7);
    text-align: center;
    min-width: 0;
  }

  .weekday {
    font-weight: 600;
    padding: 0.15rem 0;
  }

  .day-cell {
    position: relative;
    border: 1px solid transparent;
    background: transparent;
    padding: 0.35rem 0 0.55rem;
    border-radius: 6px;
    cursor: pointer;
    line-height: 1.1;

    &:hover:not(:disabled) {
      background: rgba(0, 103, 192, 0.08);
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }

    &.is-outside {
      opacity: 0.35;
    }

    &.is-today:not(.is-selected) {
      border-color: rgba(0, 103, 192, 0.35);
    }

    &.is-selected {
      background: rgba(0, 103, 192, 0.15);
      border-color: #0067c0;
      font-weight: 600;
    }
  }

  .day-num {
    display: block;
    font-size: 0.9rem;
  }

  .slot-dot {
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #0067c0;
    margin: 2px auto 0;
  }
}

.enrol-slot-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: stretch;

  .enrol-slot-tile {
    flex: 0 1 auto;
    min-width: 9.5rem;
    min-height: 2.25rem;
    padding: 0.4rem 0.65rem;
    border: 1px solid #ebe9f1;
    border-radius: 0.357rem;
    gap: 1.125rem;
    /* Ten sam akcent co kropki / hover w kalendarzu na tym widoku (#0067c0) */
    background-color: rgba(0, 103, 192, 0.055);
  }

  .enrol-slot-time {
    min-width: 3.25rem;
    letter-spacing: 0.02em;
  }

  /* Zajęty: wypełnienie jak primary, wygaszone — ten sam niebieski co kalendarz */
  .enrol-slot-btn-full.btn-primary:disabled,
  .enrol-slot-btn-full.btn-primary.disabled {
    opacity: 1 !important;
    color: #fff !important;
    background-color: rgba(0, 103, 192, 0.42) !important;
    border-color: rgba(0, 103, 192, 0.32) !important;
    box-shadow: none !important;
  }
}

.enrol-cert-block:not(:last-child) {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #ebe9f1;
}

.enrol-cert-meta-list > li {
  display: flex;
  align-items: center;
  line-height: 1.35;

  &:not(:last-child) {
    margin-bottom: 0.5rem;
  }

  .feather-icon {
    display: flex;
    align-items: center;
    align-self: center;
  }
}

.enrol-cert-meta-list .enrol-cert-detail-link {
  color: #0067c0;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: #005099;
    text-decoration: underline;
  }
}
</style>
